import { useState, useRef, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
// import { toast } from "react-toastify";

import { 
  Alert,
  Box, Button, 
  Chip,
  Collapse,
  Divider,
  FormControl, FormControlLabel,
  TextField, InputAdornment,
  IconButton,
  MenuItem, 
  OutlinedInput,
  Paper,
  Select, Switch,
  Stack,
  Typography, 
  useTheme 
} from "@mui/material";

//import styles from "../../main/main.module.css";
import styled from "styled-components";

import SearchIcon from "@mui/icons-material/Search"; // MUI의 검색 아이콘
import DescriptionIcon from "@mui/icons-material/Description"; // MUI 문서 아이콘
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import VerifiedIcon from "@mui/icons-material/Verified";

import dayjs from "dayjs";

import { 
  useGetTableData, useGetTableDataInQueryString,
  useCreateTableData, 
  usePutTableData, 
  useDeleteTableData 
} from "../../../components/Table/hooks";

import { 
  strategyRegisterColumns, strategyRegisterColumns_admin,
  strategyEmpSearchColumns,
  ImageRenderState,
} from "../../../constants/columns";

import { NewTableTemplate } from "../../../components/Table/TableTemplate";
import { ModalTemplate } from "../../../components/Table/ModalTemplate";
import { ImageUploader } from "../../../components/Table/ImageUploader";    
import Buttons from "../../../../common/Buttons";
import { EmptyState } from "../../../components/common/EmptyState";
import { 
  BreadcrumbsTable,
  ConfirmDialog, ConfirmDialogSingle,
  deepCopy,
  SnackbarAlert, 
  formatDateWithDayjs, formatDateToDayjs,
} from "../../../components/common/HelperFunc";
import * as message from "../../../constants/messages";
import { FONT_PROPS, COLORS } from "../../../styles/colors";

///////////////////////////////
// 스타일
///////////////////////////////

// 테이블 컨테이너 스타일
const TableContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 16px 32px 16px 32px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 600px) {
    padding: 12px;
    overflow: auto;
  }
`;

//////////////////////////////////
// 위험성 개선대책 수립 컴포넌트
//////////////////////////////////
const PARENT_TITLE = "대책관리";
const CHILD_TITLE = "위험성 개선대책 수립";

const toNumber = (value) => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const hasRegisteredImprove = (row) => toNumber(row?.improve_status) >= 10;
const hasCompletedImprove = (row) => toNumber(row?.improve_status) >= 20;
const isOverdueImprove = (row) => (
  row?.expected_date &&
  !hasCompletedImprove(row) &&
  dayjs(row.expected_date).isBefore(dayjs(), "day")
);
const hasMissingPlanPhoto = (row) => hasRegisteredImprove(row) && !row?.improve_before;
const hasFocusMatch = (row, focus) => {
  if (!focus?.hasFocus) return false;
  return (
    (focus.evalId && Number(row?.eval_id) === focus.evalId) ||
    (focus.improveId && Number(row?.improve_id) === focus.improveId) ||
    (focus.registerCauseId && Number(row?.register_cause_id) === focus.registerCauseId)
  );
};

const STRATEGY_FILTERS = [
  { key: "all", label: "전체" },
  { key: "focus", label: "선택 항목" },
  { key: "waiting", label: "대책 미수립" },
  { key: "registered", label: "진행중" },
  { key: "overdue", label: "기한 초과" },
  { key: "completed", label: "개선 완료" },
  { key: "photo", label: "사진 확인 필요" },
];

export function StrategyRegister() {
  const { group_code, project_code } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const focusContext = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const stateFocus = location.state?.focusImprove || {};
    const evalId = Number(params.get("eval_id") || stateFocus.eval_id || 0) || null;
    const improveId = Number(params.get("improve_id") || stateFocus.improve_id || 0) || null;
    const registerCauseId = Number(params.get("register_cause_id") || stateFocus.register_cause_id || 0) || null;
    const machineId = params.get("machine_id") || stateFocus.machine_id || null;
    const year = Number(params.get("year") || 0) || null;
    const month = Number(params.get("month") || 0) || null;

    return {
      evalId,
      improveId,
      registerCauseId,
      machineId,
      year,
      month,
      source: params.get("source") || null,
      hasFocus: Boolean(evalId || improveId || registerCauseId),
      title: stateFocus.danger_description || "",
      machineName: stateFocus.safe_machine_name || stateFocus.machine_name || "",
    };
  }, [location.search, location.state]);

  ////////////////////////
  // CRUD 요청
  ////////////////////////

  // 데이터 조회에 필요한 조건을 상태로 관리합니다. (평가연도, 평가월, 설비명)
  const [searchStatus, setSearchStatus] = useState({});
  const [strategyFilter, setStrategyFilter] = useState(focusContext.hasFocus ? "focus" : "all");
  // 데이터 조회에 필요한 쿼리스트링을 만듭니다.
  const qeuryString = { 
    params: { // 쿼리스트링 만들때 params 이름을 사용하는게 규칙입니다.
      year: searchStatus.year,
      month: searchStatus.month,
      machine_id: searchStatus.machine_id,
    },
  }

  // 위험요인등록 현황 조회 요청
  const getRegisterStatusData = useGetTableData(
    "request",
    "/getRegisterStatus", 
    group_code,
    project_code
  );

  // 데이터 조회 요청
  const getData = useGetTableDataInQueryString(
    "request",
    "/getStrategyRegister",
    group_code,
    project_code,
    qeuryString,
  );
  // 데이터 생성 요청
  const postData = useCreateTableData( 
    "request",
    "/postStrategy",
    group_code,
    project_code
  );
  // 데이터 수정 요청
  const putData = usePutTableData(
    "request",
    "/putStrategy",
    group_code,
    project_code
  );
  // 데이터 삭제 요청
  const deleteData = useDeleteTableData(
    "request",
    "/deleteStrategy",
    group_code,
    project_code
  );

  // 필터링 목록 조회 요청 (평가년도별, 평가년도의 최신 평가 월, register_cause_id을 가져옵니다.)
  const getFilterListData = useGetTableData(
    "request",
    "/getStrategyRegisterFilterList",
    group_code,
    project_code,
  );

  // 개선전.후 이미지 생성/수정 요청
  const postImproveImageData = useCreateTableData(
    "request",
    "/postStrategyResultImage",
    group_code,
    project_code
  );

  // 데이터 업데이트 알람 상태 관리
  const [dataAlert, setDataAlert] = useState({
    isOpen: false,
    message: '',
    severity:'' // 'success' | 'error' | 'warning' | 'info'
  });
  const openDataAlert = (message, severity = 'success') => {
    setDataAlert({
      ...dataAlert,
      isOpen: true,
      message: message,
      severity: severity
    });
  };
  const closeDataAlert = () => {
    setDataAlert({
      ...dataAlert,
      isOpen: false,
      message: '',
      severity: '',
    });
  };

  // 메시지 박스의 알람 메시지
  const [messageAlarm, setMessageAlarm] = useState({ isOpen: false, title: '', message: '' });
  const openAlarmMessage = (title='', message='',) => setMessageAlarm({ isOpen: true, title: title, message: message });
  const closeAlarmMessage = () => setMessageAlarm({ isOpen: false, title: '', message: '' });

  // 담당자 모달 창 상태 관리
  const [searchEmployee, setSearchEmployee] = useState({ isOpen: false, rowData: null });
  const openSearchEmployee = (rowData) => setSearchEmployee({ isOpen: true, rowData: rowData });
  const closeSearchEmployee = () => setSearchEmployee({ isOpen: false, rowData: null });
  
  // 개선전 사진 모달 상태 관리
  const [showImproveImage, setShowImproveImage] = useState({ isOpen: false, state: ImageRenderState.NONE, rowData: null });
  const openShowImproveImage = (state, rowData) => setShowImproveImage({ isOpen: true, state: state, rowData: rowData });
  const closeShowImproveImage = () => setShowImproveImage({ isOpen: false, state: ImageRenderState.NONE, rowData: null });

  // 필터 초기값 설정
  useEffect(() => {
    // 데이터가 로드되지 않았거나 비어 있는 경우 처리 중단
    if(!getFilterListData.data || getFilterListData.data.length === 0 ) {
      return;
    }
    if(!getRegisterStatusData.data || getRegisterStatusData.data.length === 0) {
      return;
    }
    
    // 최신 평가연도 데이터 가져오기
    let latestYearData = null;
    for(let i = getFilterListData.data.length - 1; i >= 0; i--) {
      if(getFilterListData.data[i].eval_date_year &&
        getFilterListData.data[i].latest_month &&
        getFilterListData.data[i].register_cause_id) {
        latestYearData = getFilterListData.data[i];
        break;
      }
    }
    if(!latestYearData) {
      return; // 최신 평가연도 데이터가 없으면 처리 중단
    }

    const data = 
    {
      year: focusContext.year || latestYearData.eval_date_year,
      month: focusContext.month || Number(latestYearData.latest_month),
      machine_id: 'all', // 모든 설비를 조회하기 위해 'all'로 설정합니다.
    };
    data.machine_id = focusContext.machineId || data.machine_id;
    
    setSearchStatus(data);
  }, [getFilterListData.data, getRegisterStatusData.data, focusContext.machineId, focusContext.month, focusContext.year]);

  useEffect(() => {
    if (focusContext.hasFocus) {
      setStrategyFilter("focus");
    }
  }, [focusContext.evalId, focusContext.hasFocus, focusContext.improveId, focusContext.registerCauseId]);

  // 임시 데이터 상태 관리
  const [tempGetData, setTempGetData] = useState(null);
  const handleSetTempGetData = (data) => setTempGetData(data);
  useEffect(() => {
    const tempData = deepCopy(getData.data || []);
    for(let i = 0; i < tempData.length; ++i) {
      // 데이터에 인덱스 추가(행 편집 용도로 사용)
      tempData[i].__index = i;
      // 담당자 설정 모달 활성화 여부를 나타내는 상태 변수
      tempData[i].__hasSearchEmployee = false;
      // 개선전 사진 사진 추가, 수정, 확대 상태 변수
      tempData[i].__imageStatus = ImageRenderState.NONE;
      // 개선전 사진 url 추가 (행 수정 후 이미지 업로드를 위해 필요)
      tempData[i].__improveBeforeUrl = tempData[i].improve_before;
      // 개선후 사진 url 추가 (행 수정 후 삭제를 위해 필요)
      tempData[i].__improveAfterUrl = tempData[i].improve_after;
      // 개선 예정일 날짜 추가 (관련된 날짜 포맷 변경을 위해 필요)
      tempData[i].__expectedDate = tempData[i].expected_date;

      // 개선 예정일 날짜 포맷 변경
      tempData[i].expected_date = formatDateToDayjs(tempData[i].expected_date);
      const isFocused = hasFocusMatch(tempData[i], focusContext);
      const isOverdue = isOverdueImprove(tempData[i]);
      const isWaiting = !hasRegisteredImprove(tempData[i]);
      tempData[i].__bgColor = isFocused
        ? "#eff6ff"
        : isOverdue
          ? "#fff7ed"
          : isWaiting
            ? "#f8fafc"
            : undefined;
    }
    handleSetTempGetData(tempData);
  },[focusContext, getData.data]);

  const strategySummary = useMemo(() => {
    const rows = tempGetData || [];
    const waitingCount = rows.filter((row) => !hasRegisteredImprove(row)).length;
    const registeredCount = rows.filter((row) => hasRegisteredImprove(row) && !hasCompletedImprove(row)).length;
    const completedCount = rows.filter(hasCompletedImprove).length;
    const overdueCount = rows.filter(isOverdueImprove).length;
    const photoMissingCount = rows.filter(hasMissingPlanPhoto).length;
    const focusedCount = rows.filter((row) => hasFocusMatch(row, focusContext)).length;

    return {
      total: rows.length,
      waitingCount,
      registeredCount,
      completedCount,
      overdueCount,
      photoMissingCount,
      focusedCount,
    };
  }, [focusContext, tempGetData]);
  const filteredStrategyRows = useMemo(() => {
    const rows = tempGetData || [];
    if (strategyFilter === "focus") {
      return focusContext.hasFocus ? rows.filter((row) => hasFocusMatch(row, focusContext)) : rows;
    }
    if (strategyFilter === "waiting") return rows.filter((row) => !hasRegisteredImprove(row));
    if (strategyFilter === "registered") return rows.filter((row) => hasRegisteredImprove(row) && !hasCompletedImprove(row));
    if (strategyFilter === "overdue") return rows.filter(isOverdueImprove);
    if (strategyFilter === "completed") return rows.filter(hasCompletedImprove);
    if (strategyFilter === "photo") return rows.filter(hasMissingPlanPhoto);
    return rows;
  }, [focusContext, strategyFilter, tempGetData]);
  const isProcessing = Boolean(postData.isPending || postData.isLoading || putData.isPending || putData.isLoading || deleteData.isPending || deleteData.isLoading);

  // 평가연도 목록 (중복 제거를 위한 작업)
  const yearList = Array.from(
    new Set(getFilterListData.data.map(item => item.eval_date_year))
  );
  // 설비명 목록 (중복 제거를 위한 작업)
  const machineIdList = Array.from(
    new Map(
      getRegisterStatusData.data.map((item) => [
        item.machine_id, 
        { 
          machine_id: item.machine_id, 
          machine_name: item.machine_name 
        }
      ])
    ).values()
  );
  machineIdList.unshift({ machine_id: 'all', machine_name: '전체' }); // 전체 추가
  //console.log(machineIdList);

  // 감소대책 가이드 모달 상태 관리
  const [strategyGuide, setStrategyGuide] = useState({ isOpen: false });
  const openStrategyGuide = () => setStrategyGuide({ isOpen: true });
  const closeStrategyGuide = () => setStrategyGuide({ isOpen: false });
  // 감소대책 가이드 데이터
  const guideData = [
    {id: 1, title: "1.본질적(근원적) 대책", content: "위험한 작업의 폐지.변경, 위험물질 또는 유해.위험요인이 보다 적은 재료로의 대체, 설계나 계획단계에서 위험성을 제거 또는 저감하는 조치"},
    {id: 2, title: "2.공학적 대책", content: "인터록, 방호장치, 방책, 국소배기장비 설치 등의 조치"},
    {id: 3, title: "3.관리적 대책", content: "매뉴얼 정비, 출입금지, 노출관리, 교육훈련 등의 조치"},
    {id: 4, title: "4.개인보호구의 사용", content: "상기1~3의 조치를 취하더라도 제거.감소할 수 없었던 위험성에 대해서만 실기하거나 상기 조치 외의 추가적인 조치로 사용"},
  ];
  const renderGuide = () => {
    return guideData.map((item, index) => (
      <Box key={item.id}>
        {/* 감소대책 가이드 타이틀, 컨텐츠 표시 */}
        <Box sx={{ display: 'flex', gap: 1, }}>
          <Typography variant="h6" component="div"
            sx={{ 
              display: 'flex',
              width: '160px',
              borderRadius: 1,
              backgroundColor: '#12769B',
              padding: "10px 16px",
              fontFamily: FONT_PROPS.PRIMARY,
              fontWeight: FONT_PROPS.MEDIUM,
              fontSize: "14px",   
              color: "#ffffff",
            }}
          >
            {item.title}
          </Typography>
          <Typography variant="h6" component="div"
            sx={{ 
              display: 'flex',
              width: '400px',
              borderRadius: 1,
              backgroundColor: '#B3EDC8',
              padding: "10px 16px 10px 16px",
              fontFamily: FONT_PROPS.PRIMARY,
              fontWeight: FONT_PROPS.MEDIUM,
              fontSize: "13px",   
              color: FONT_PROPS.DARK,
            }}
          >
            {item.content}
          </Typography>
        </Box>

        {/* 마지막 인덱스가 아닐 때만 화살표 렌더링 */}
        {index !== guideData.length - 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', margin: '16px' }}>
            <DoubleArrowIcon sx={{ color: '#12769B', transform: 'rotate(90deg)' }}/>
          </Box>
        )}
      </Box>
    ));
  };

  // 테이블 참조
  const tableRef = useRef();
  const handleSelectedReset = () => { tableRef.current?.clearSelection(); };
  const handleGetSelectedRows = () => { return tableRef.current?.getSelectedRows(); };

  // 테이블의 상단 툴바 커스터마이징 관련 내용
  const [showSearch, setShowSearch] = useState(false); // 검색창 열림 여부
  const [globalFilter, setGlobalFilter] = useState(''); // 검색 텍스트 상태
  const [showAdminMode, setShowAdminMode] = useState(false); // 관리자 모드 활성화 여부
  const customTableTopToolbar = (table) => (
    <Box width="100%" display="flex" justifyContent="space-between" alignItems="center" p={2}>
      {/* 왼쪽 영역 */}
      <Box display={"flex"} alignItems="center" gap={4} marginLeft={'16px'}>
        {/* 평가연도 카테고리 */}
        <FormControl size="small">
          <Box sx={{display: "flex",alignItems: "center",gap: "16px",}}>
            <Typography sx={{fontWeight: FONT_PROPS.SEMIBOLD,fontSize: 15,color: theme.text,}}>
              평가연도
            </Typography>
            <Select
              value={searchStatus.year || ""}
              displayEmpty
              onChange={(e) => {
                setSearchStatus({
                    ...searchStatus,
                    year: e.target.value,
                });
              }}
              sx={{width: "100px",fontWeight: FONT_PROPS.SEMIBOLD,color: theme.text,fontSize: 15,}}
            >
              {/* 필터 목록 */}
              {yearList.reverse().map((year, index) => (
                <MenuItem key={index} value={year} sx={{fontWeight: FONT_PROPS.SEMIBOLD,color: theme.text,fontSize: 15,}}>
                  {year}년
                </MenuItem>
              ))}
            </Select>
          </Box>
        </FormControl>
        {/* 평가월 카테고리 */}
        <FormControl size="small">
          <Box sx={{display: "flex",alignItems: "center",gap: "16px",}}>
            <Typography sx={{fontWeight: FONT_PROPS.SEMIBOLD,fontSize: 15,color: theme.text,}}>
              평가월
            </Typography>
            <Select
              value={searchStatus.month || ""}
              displayEmpty
              onChange={(e) => {
                  setSearchStatus({
                      ...searchStatus,
                      month: e.target.value,
                  });
              }}
              sx={{ width: "80px",fontWeight: FONT_PROPS.SEMIBOLD,color: theme.text,fontSize: 15,}}
            >
              {/* 필터 목록 */}
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].reverse().map((month, index) => (
                <MenuItem key={index} value={month} sx={{fontWeight: FONT_PROPS.SEMIBOLD,color: theme.text,fontSize: 15,}}>
                  {month}월
                </MenuItem>
              ))}
            </Select>
          </Box>
        </FormControl>
        {/* 설비명 카테고리 */}
        <FormControl size="small">
          <Box sx={{display: "flex",alignItems: "center",gap: "16px",}}>
            <Typography sx={{fontWeight: FONT_PROPS.SEMIBOLD,fontSize: 15,color: theme.text,}}>
              설비·구역명
            </Typography>
            <Select
              value={searchStatus.machine_id || ""}
              displayEmpty
              onChange={(e) => {
                setSearchStatus({
                  ...searchStatus,
                  machine_id: e.target.value,
                });
              }}
              sx={{minWidth: "120px",fontWeight: FONT_PROPS.SEMIBOLD,color: theme.text,fontSize: 15,}}
            >
              {/* 필터 목록 */}
              {machineIdList.map((data, index) => (
                <MenuItem key={index} value={data.machine_id} sx={{fontWeight: FONT_PROPS.SEMIBOLD,color: theme.text,fontSize: 15,}}>
                  {data.machine_name}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </FormControl>
        {/* 감소대책 수립 가이드 버튼 */}
        <Button
          variant="outlined"
          startIcon={<DescriptionIcon />}
          onClick={() => {
            //console.log("감소대책 수립 가이드 클릭!!");
            openStrategyGuide();
          }}
          sx={{ fontWeight: FONT_PROPS.MEDIUM,fontSize: '15px',padding: "8px 16px 8px 16px",mr: "8px",}}
        >
          감소대책 수립 가이드
        </Button>
      </Box>
      {/* 오른쪽 영역 - 기존 내부 액션도 원하면 여기에 직접 렌더링 */}
      <Box display="flex" alignItems="center" gap={2}>
        {/* 검색창 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => setShowSearch((prev) => !prev)}>
            <SearchIcon />
          </IconButton>
        
          <Collapse in={showSearch} orientation="horizontal">
          <TextField
            size="small"
            placeholder="검색"
            value={globalFilter}
            onChange={(e) => {
              setGlobalFilter(e.target.value);
              table.setGlobalFilter(e.target.value);
            }}
            sx={{ minWidth: 200 }}
            variant="outlined"
            slots={{ input: OutlinedInput,}}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => {
                        setGlobalFilter('');
                        table.setGlobalFilter('');
                        setShowSearch(false); // 닫기
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }
            }}
          />
          </Collapse>
        </Box>
        {/* 삭제버튼 */}
        {showAdminMode && 
          <Buttons.Delete 
            onClick={openDeleteModal} // 행 삭제 로직 실행
            disabled={table.getSelectedRowModel().rows.length === 0 || isProcessing}
            sx={{ padding: '12px 16px' }}
          >
            데이터 삭제
          </Buttons.Delete>
        }
        {/* 관리자 모드 */}
        <FormControl component="fieldset">
          <FormControlLabel
            value="top"
            control={
              <Switch color="primary" 
                checked={showAdminMode}
                onChange={(event) => {
                  setShowAdminMode(event.target.checked);
                  handleSelectedReset();
                }}
              />
            }
            label="관리자모드"
            labelPlacement="end" // bottom,end,start,top
            sx={{marginLeft : '4px'}}
          />
        </FormControl>
      </Box>
    </Box>
  );

  // 행 편집 작업 (위험성 감소대책, 개선 후 위험성, 개선 예정일, 담당자)
  const changeRowData = ({ values, row, table }) => {
    //console.log("✅ 편집 호출 완료!", values);
    //console.log("✅ 편집 호출 완료!", row.original);

    // 개선전 사진 등록 모달 열기
    if(values.__imageStatus !== ImageRenderState.NONE) {
      openShowImproveImage(values.__imageStatus, {...values});
      return;
    }

    // 담당자 검색 모달 표시
    if(values.__hasSearchEmployee) {
      openSearchEmployee({...values}); // 위험성 감소대책 일정관리의 행 데이터 저장
      return;
    }

    // 편집된 데이터 변경
    const findData = tempGetData.find(item => {
      return item.__index === row.original.__index;
    });
    Object.keys(values).forEach((key) => {
      if (findData[key] !== values[key]) {
        findData[key] = values[key];
      }
    });

    // TempGetData 변경
    const tempData = tempGetData.map(item => {
      return item.__index === row.original.__index ? findData : item;
    });
    handleSetTempGetData(tempData);
  };
  // 위험성 개선대책 수립 테이블의 전체 행 데이터 편집 완료 액션
  const completeEditAllConfirm = !showAdminMode ? (
    <Buttons.Custom
      startIcon={<CheckIcon />}
      disabled={isProcessing}
      onClick={(event) =>{
        event.stopPropagation(); // 이벤트 전파 중단
        //
        const findDatas = (tempGetData || []).filter((item) => {
          // 1. improve_plan, improve_after_risk, expected_date, emp_id 속성의 데이터가 있는지 확인
          // 2. improve_status 속성의 데이터가 없거나, 있더라도 일정 등록완료 전인지 확인
          const hasDatas = item.improve_plan && item.improve_after_risk && item.expected_date && item.emp_id && item.improve_before;
          const isNotExpected = !item.improve_status || item.improve_status < 10; // 10: 대책 등록완료
          return hasDatas && isNotExpected;
        });
          
        if(findDatas.length <= 0) {
          openAlarmMessage(
            '안내',
            '전체 저장할 항목이 없습니다.'
          );
          return;
        }
        addEditData(findDatas);
      }}
      sx={{ display: 'flex', alignItems: 'center', padding: '8px 12px' }}
    >
      전체 저장
    </Buttons.Custom>
  ) : (
    <Buttons.Custom
      startIcon={<CheckIcon />}
      disabled={isProcessing}
      onClick={(event) =>{
        event.stopPropagation(); // 이벤트 전파 중단
        const findDatas = (tempGetData || []).filter((item) => {
          // 등록대기는 수정 불가
          const isWating = item?.improve_status ? true : false;
          // 개선 예정일이 유효한지 확인
          const isValidDate = dayjs(item.expected_date).isValid();
          return isWating && isValidDate;
        });
          
        if(findDatas.length <= 0) {
          openAlarmMessage(
            '안내',
            '전체 수정할 항목이 없습니다.'
          );
          return;
        }
        
        updateEditDataByAdmin(findDatas);
      }}
      sx={{ display: 'flex', alignItems: 'center', padding: '8px 12px' }}
    >
      전체 수정
    </Buttons.Custom>
  );
  // 위험성 개선대책 수립 테이블의 개별 행 데이터 편집 완료 액션
  const completeEditConfirm = (rowData) => {
    return(
      <>
        {!showAdminMode ? (
          <Buttons.Custom
            //startIcon={<CheckIcon />}
            disabled={rowData.improve_status >= 10 || isProcessing} // 10: 대책 등록완료
            onClick={(event) =>{
              event.stopPropagation(); // 이벤트 전파 중단
              
              if(checkData(rowData)) {
                openAlarmMessage(
                  '안내',
                  '개선전 사진, 위험성 개선대책 수립, 개선 후 위험성, 개선 예정일, 담당자를 설정해주세요.'
                );
                return;
              }
              
              addEditData(rowData);
            }}
            sx={{ padding: '8px 12px' }}
          >
            저장
          </Buttons.Custom>
        ) : (
          <Buttons.Custom
            //startIcon={<CheckIcon />}
            disabled={!rowData?.improve_status || isProcessing} // 등록 대기는 수정 불가
            onClick={(event) =>{
              event.stopPropagation(); // 이벤트 전파 중단
              
              if(checkDataByAdmin(rowData)) {
                openAlarmMessage(
                  '안내',
                  '개선전 사진, 위험성 개선대책 수립, 개선 후 위험성, 개선 예정일, 담당자를 설정해주세요.'
                );
                return;
              }

              updateEditDataByAdmin(rowData);
            }}
            sx={{ padding: '8px 12px' }}
          >
            수정
          </Buttons.Custom>
        )}
      </>
    );
  };
  // DB 업데이트 (추가 데이터)
  const addEditData = async (rowDatas) => {
    try {
      // 배열이 아닌 경우 배열로 변환
      const tempRowDatas = Array.isArray(rowDatas)? rowDatas: [rowDatas]; 

      // 이미지를 업로드 용도의 formData로 변경
      const formatImageWithFormData = (value) => {
        const keys = [ "improve_before_image" ]; // 'keys'는 백엔드에서 이미지를 찾는 key값으로 사용함.
        // formdata로 넘겨줄 이미지 변경
        const formData = new FormData();
        formData.append(keys[0], value[keys[0]], `StrategyResult_before_${value.eval_id}`); // key, data, 이미지 파일 이름
        // formData.append(keys[1], tempData[keys[1]], `StrategyResult_after_${value.improve_id}`);
        return formData;
      }

      const updateDatas = await Promise.all( // 비동기 병렬 처리
        tempRowDatas.map(async (item) => {
          // 개선전 이미지 로컬 저장소에 저장
          const formData = formatImageWithFormData(item);
          const urls = await postImproveImageData.mutateAsync(formData);
          let url = null;
          const hasData = urls.data.url.find(item => item.key === "improve_before_image");
          if(hasData) { url = hasData.url; }

          // 데이터 가공
          return {
            improve_id: item.eval_id,
            improve_plan: item.improve_plan, 
            improve_after_risk: item.improve_after_risk,
            expected_date: formatDateWithDayjs(item.expected_date),
            emp_id: item.emp_id,
            improve_status: 10, // 상태: 10(대책 등록완료)
            improve_before: url,
          };
        })
      );

      // 데이터베이스 업데이트
      const result = await postData.mutateAsync(updateDatas);
      if(result.status === 200) {
        openDataAlert(`${updateDatas.length}건 개선대책이 저장되었습니다.`);
        await getData.refetch?.();
        handleSelectedReset();
      }
    }
    catch (error) {
      console.error("데이터 수정 중 오류 발생:", error);
      openDataAlert("개선대책 저장 중 오류가 발생했습니다.", "error");
    }
  }
  // DB 업데이트 (수정 데이터) - 관리자 모드
  const updateEditDataByAdmin = async (rowDatas) => {
    try {
      // 배열이 아닌 경우 배열로 변환
      const tempRowDatas = Array.isArray(rowDatas)? rowDatas: [rowDatas]; 

      // 이미지를 업로드 용도의 formData로 변경
      const formatImageWithFormData = (value) => {
        const formData = new FormData();
        const keys = [ 
          "improve_before_image", "beforeFilename",
          "deleteImproveAfter", "afterFilename",
        ]; // 'keys'는 백엔드에서 이미지를 찾는 key값으로 사용함.

        // 1) improve_before_image 업로드
        if (value[keys[0]] instanceof File) {
          formData.append(keys[0], value[keys[0]], `StrategyResult_before_${value.eval_id}`); // 새 이미지 파일
          if(value.__improveBeforeUrl) {
            const filename = value.__improveBeforeUrl.split('/').pop();
            formData.append(keys[1], filename); // 기존 이미지 파일 이름
          }
        }

        // 2) improve_after_image 삭제만 하고 싶을 때 (새 파일은 없음)
        if(value.__improveAfterUrl) {
          const filename = value.__improveAfterUrl.split('/').pop();
          formData.append(keys[3], filename);
          formData.append(keys[2], "true"); // 문자열로 보내기
        }

        return formData;
      };

      const updateData = await Promise.all( // 비동기 병렬 처리
        tempRowDatas.map(async (item) => {
          // 이미지 로컬 저장소에 저장
          const formData = formatImageWithFormData(item);
          let url = item.__improveBeforeUrl
          if ([...formData.entries()].length > 0) {
            // 실제로 이미지 변경 있는 행만 API 호출
            const urls = await postImproveImageData.mutateAsync(formData);
            const hasData = urls.data.url.find(item => item.key === "improve_before_image");
            if(hasData) { url = hasData.url; }
          }

          // 개선일자가 변경되었는지 확인 후 반영
          const __expectedDate = formatDateWithDayjs(item.__expectedDate);
          const expectedDate = formatDateWithDayjs(item.expected_date);
          const isSame = dayjs(__expectedDate).isSame(dayjs(expectedDate), 'day');
          let createDate = item.create_date;
          let updateDate = item.improve_update_date;
          if(!isSame) { 
            createDate = expectedDate;
            updateDate = expectedDate; 
          }

          // 데이터 가공
          return {
            improve_id: item.eval_id,
            improve_plan: item.improve_plan, 
            improve_after_risk: item.improve_after_risk,
            emp_id: item.emp_id,
            expected_date: expectedDate,
            comp_date: null,
            improve_status: 10, // 상태: 10(대책 등록완료)
            improve_before: url, 
            improve_after: null,
            create_date: createDate,
            update_date: updateDate,
          };
        })
      );

      // 데이터베이스 업데이트
      const result = await putData.mutateAsync({ updateData });
      if(result.status === 200) {
        openDataAlert(`${updateData.length}건 개선대책 수정이 완료되었습니다.`);
        await getData.refetch?.();
        handleSelectedReset();
      }
    }
    catch (error) {
      console.error("데이터 수정 중 오류 발생:", error);
      openDataAlert("개선대책 수정 중 오류가 발생했습니다.", "error");
    }
  }

  // 위험성 개선대책 수립 삭제 모달 상태 관리
  const [deleteModal, setDeleteModal] = useState({ isOpen: false });
  const openDeleteModal = () => setDeleteModal({ isOpen: true });
  const closeDeleteModal = () => setDeleteModal({ isOpen: false });
  
  // DB 업데이트 (삭제 데이터)
  const deleteEditData = async () => {
    closeDeleteModal();
    //console.log('✅ 확인 눌림 → 다음 동작 실행');
    try {

      // 선택된 행(row) 가져오기
      const selectedRows = handleGetSelectedRows().map((row) => row.original);
      const filteredRows = selectedRows.filter(item => item.improve_id);
      // 선택된 행(row)의 id 가져오기
      const selectedIds = filteredRows.map((row) => row.improve_id);

      if(selectedIds.length <= 0) {
        openAlarmMessage(
          '안내',
          `대책상태가 '등록완료' 또는 '개선완료'인 데이터만 삭제할 수 있습니다.`
        );
        return;
      }

      // 데이터베이스 업데이트
      const result = await deleteData.mutateAsync({
        deleteParam: 'improve_id',
        param: selectedIds,
      });

      if(result.status === 200) {
        openDataAlert(`${selectedIds.length}건 개선대책이 삭제되었습니다.`);
        await getData.refetch?.();
      }

      // 선택 상태 초기화
      handleSelectedReset();
    }
    catch (error) {
      console.error("데이터 삭제 중 오류 발생:", error);
      openDataAlert("개선대책 삭제 중 오류가 발생했습니다.", "error");
    }
  };

  //////////////////////////////////
  // 개선전.후 사진 관련 변수 및 함수
  //////////////////////////////////

  // 개선전 사진 등록
  const addBeforeImage = (image) =>{
    //console.log(image);
    if(!image) {
      openAlarmMessage(
        '안내',
        message.retryImageError,
      );
      return;
    }

    // 편집된 데이터 변경
    const selectedData = {...showImproveImage.rowData};
    selectedData.improve_before_image = image.imageFile; // 개선전 사진 파일
    selectedData.improve_before = image.previewUrl; // 개선전 사진 미리보기 URL
    selectedData.__imageStatus = ImageRenderState.NONE; // 이미지 상태 none

    // TempGetData 변경
    const tempData = tempGetData.map((item) => {
      return item.__index === selectedData.__index ? selectedData : item; 
    });

    handleSetTempGetData(tempData);
    closeShowImproveImage(); // 모달 닫기
  };
  
  /////////////////////////////
  // 담당자 등록의 변수 및 함수
  /////////////////////////////

  // 담당자 등록
  const addEmployee = (rowData) => {
    //console.log(rowData);

    // 편집된 데이터 변경
    const selectedData = {...searchEmployee.rowData};
    selectedData.__hasSearchEmployee = false; // 담당자 검색 모달창 변수 초기화
    selectedData.emp_id = rowData.emp_id; // 직원 ID 초기화
    selectedData.emp_name = rowData.emp_name; // 직원 이름 초기화

    // TempGetData 변경
    const tempData = tempGetData.map(item => {
      return item.__index === selectedData.__index ? selectedData : item
    });

    handleSetTempGetData(tempData);
    closeSearchEmployee(); // 모달 닫기
  }

  ////////////////////////
  // Helper Functions
  ////////////////////////
  
  // 데이터 빈칸 체크
  const checkData = (data) => {
    const tempData = Array.isArray(data)? [...data]: [...[data]]; // 배열이 아닌 경우 배열로 변환 후 복제

    // 데이터가 없을 때
    if(tempData.length === 0) { 
      return true;
    }

    // 데이터가 있을 때
    const hasValidData = tempData.some(
      item => !item.improve_plan || !item.improve_after_risk || !item.expected_date || !item.emp_id || !item.improve_before
    );
    return hasValidData;
  };
  // 데이터 빈칸 체크 - 관리자 모드
  const checkDataByAdmin = (data) => {
    const tempData = Array.isArray(data)? [...data]: [...[data]]; // 배열이 아닌 경우 배열로 변환 후 복제

    // 데이터가 없을 때
    if(tempData.length === 0) { 
      return true;
    }

    // 데이터가 있을 때
    for(const item of tempData) {
      const date = dayjs(item.expected_date).isValid(); // 날짜 형식이 맞지 않으면 NaN 반환
      const hasValidData = !item.improve_before || !item.improve_plan 
        || !item.improve_after_risk || !date || !item.emp_id;

      if(hasValidData) {
        return true;
      }
    }
    return false;
  };

  const goAssessmentResult = () => {
    navigate(`/safe/${group_code}/${project_code}/assessment/result`);
  };

  const clearStrategyFocus = () => {
    setStrategyFilter("all");
    navigate(`/safe/${group_code}/${project_code}/strategy/register`, { replace: true });
  };

  const getStrategyFilterCount = (key) => {
    if (key === "focus") return strategySummary.focusedCount;
    if (key === "waiting") return strategySummary.waitingCount;
    if (key === "registered") return strategySummary.registeredCount;
    if (key === "overdue") return strategySummary.overdueCount;
    if (key === "completed") return strategySummary.completedCount;
    if (key === "photo") return strategySummary.photoMissingCount;
    return strategySummary.total;
  };

  const strategySummaryItems = [
    {
      label: "관리 대상",
      value: strategySummary.total,
      icon: <BuildCircleIcon />,
      tone: "#2563eb",
    },
    {
      label: "등록 대기",
      value: strategySummary.waitingCount,
      icon: <PendingActionsIcon />,
      tone: "#f97316",
    },
    {
      label: "대책 등록",
      value: strategySummary.registeredCount,
      icon: <AssignmentTurnedInIcon />,
      tone: "#7c3aed",
    },
    {
      label: "개선 완료",
      value: strategySummary.completedCount,
      icon: <VerifiedIcon />,
      tone: "#16a34a",
    },
    {
      label: "기한 초과",
      value: strategySummary.overdueCount,
      icon: <EventBusyIcon />,
      tone: "#dc2626",
    },
    {
      label: "사진 확인",
      value: strategySummary.photoMissingCount,
      icon: <DescriptionIcon />,
      tone: "#0f766e",
    },
  ];

  const renderStrategySummary = () => (
    <Paper
      variant="outlined"
      sx={{
        mb: 2,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      <Box
        sx={{
          p: 2,
          display: "flex",
          gap: 2,
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              color={strategySummary.overdueCount > 0 ? "error" : "primary"}
              label={strategySummary.overdueCount > 0 ? "기한 초과 확인" : "개선대책 관리"}
            />
            <Typography sx={{ fontSize: 18, fontWeight: 800, color: "text.primary" }}>
              승인된 개선 필요 항목
            </Typography>
          </Stack>
          <Typography sx={{ mt: 0.5, fontSize: 13, color: "text.secondary" }}>
            평가 결과에서 승인 완료된 개선 필요 항목만 이 화면에 표시됩니다.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<FactCheckIcon />}
          onClick={goAssessmentResult}
          sx={{ borderRadius: 1.5, fontWeight: 700, whiteSpace: "nowrap", width: { xs: "100%", sm: "auto" } }}
        >
          평가 결과로 이동
        </Button>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            md: "repeat(6, minmax(0, 1fr))",
          },
        }}
      >
        {strategySummaryItems.map((item, index) => (
          <Box
            key={item.label}
            sx={{
              minWidth: 0,
              p: 2,
              borderRight: {
                md: index === strategySummaryItems.length - 1 ? "none" : `1px solid ${theme.palette.divider}`,
              },
              borderBottom: {
                xs: index < 4 ? `1px solid ${theme.palette.divider}` : "none",
                md: "none",
              },
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "8px",
                  display: "grid",
                  placeItems: "center",
                  color: item.tone,
                  bgcolor: `${item.tone}14`,
                  flex: "0 0 auto",
                }}
              >
                {item.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 700 }}>
                  {item.label}
                </Typography>
                <Typography sx={{ fontSize: 24, color: "text.primary", fontWeight: 850, lineHeight: 1.1 }}>
                  {item.value}
                </Typography>
              </Box>
            </Stack>
          </Box>
        ))}
      </Box>

      <Divider />

      {focusContext.hasFocus && (
        <Alert
          severity={strategySummary.focusedCount > 0 ? "success" : "warning"}
          action={
            <Button color="inherit" size="small" onClick={clearStrategyFocus}>
              전체 보기
            </Button>
          }
          sx={{ borderRadius: 0 }}
        >
          평가 결과에서 선택한 항목을 확인 중입니다.
          {focusContext.machineName ? ` 설비·구역: ${focusContext.machineName}` : ""}
          {focusContext.title ? ` / 위험요인: ${focusContext.title}` : ""}
          {strategySummary.focusedCount <= 0 ? " 현재 조회 조건에서는 같은 항목을 찾지 못했습니다." : ""}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          p: 1.5,
          borderTop: focusContext.hasFocus ? `1px solid ${theme.palette.divider}` : "none",
        }}
      >
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {STRATEGY_FILTERS.filter((filter) => filter.key !== "focus" || focusContext.hasFocus).map((filter) => (
            <Chip
              key={filter.key}
              label={`${filter.label} ${getStrategyFilterCount(filter.key)}건`}
              variant={strategyFilter === filter.key ? "filled" : "outlined"}
              color={strategyFilter === filter.key ? "primary" : "default"}
              onClick={() => setStrategyFilter(filter.key)}
              sx={{ fontWeight: 800 }}
            />
          ))}
        </Stack>
        <Typography sx={{ fontSize: 12, color: "text.secondary", fontWeight: 800 }}>
          현재 {filteredStrategyRows.length}건 표시
        </Typography>
      </Box>

      <Alert severity={strategySummary.total > 0 ? "info" : "warning"} sx={{ borderRadius: 0 }}>
        {strategySummary.total > 0
          ? "등록 대기 항목은 담당자, 예정일, 개선 전 사진, 개선대책을 입력한 뒤 저장하면 대책 등록 상태가 됩니다."
          : "표시할 개선대책 대상이 없습니다. 평가 결과 화면에서 평가를 승인하면 개선 필요 항목이 이곳으로 연결됩니다."}
      </Alert>
    </Paper>
  );

  ////////////////////////
  // 렌더링
  ////////////////////////
  function renderData() {
    return (
      <TableContainer>
        {renderStrategySummary()}
        {/* 위험성 개선대책 수립 테이블 */}
        {!getData.isLoading && !filteredStrategyRows.length ? (
          <EmptyState
            icon={<BuildCircleIcon />}
            title="개선대책을 수립할 항목이 없습니다"
            description="평가 결과 화면에서 개선 필요 평가를 승인하면 담당자, 예정일, 개선 전 사진을 등록할 대상이 이곳에 표시됩니다."
            actionLabel="평가 결과로 이동"
            actionIcon={<FactCheckIcon />}
            onAction={goAssessmentResult}
          />
        ) : (
          <NewTableTemplate 
            ref={tableRef}
            columns={showAdminMode ? strategyRegisterColumns_admin : strategyRegisterColumns}
            data={filteredStrategyRows || []}
            topToolbarContent={customTableTopToolbar}
            renderRowActionHeader={completeEditAllConfirm}
            renderRowActionContent={completeEditConfirm}
            renderRowActionCustomOptions={changeRowData}
            editDisplayMode={"row"}
            enableCustomGlobalFilter={true}
            enabledSelectionRow={showAdminMode}
            enabledSelectionAllRow={showAdminMode}
            templateSX={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: "0.375rem", 
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)',
            }}
          />
        )}
        {/* 개선전 사진 등록 모달 */}
        {showImproveImage.state === ImageRenderState.BEFORE && (
          <RegisterBeforeImage 
            isOpen={showImproveImage.isOpen}
            onConfirm={addBeforeImage} 
            onCancel={closeShowImproveImage} 
          />
        )}
        {/* 개선전 사진 확대 모달 */}
        {showImproveImage.state === ImageRenderState.EXPAND_BEFORE && (
          <ExpandBeforeImage 
            isOpen={showImproveImage.isOpen}
            imageURL={showImproveImage.rowData.improve_before}
            onConfirm={closeShowImproveImage}
          />
        )}
        {/* 담당자 검색 모달 */}
        <SearchEmployee 
          isOpen={searchEmployee.isOpen} 
          onConfirm={addEmployee} 
          onCancel={closeSearchEmployee} 
        />
        {/* 감소대책 수립 가이드 */}
        <ModalTemplate
          isOpen={strategyGuide.isOpen}
          handleConfirm={closeStrategyGuide}
          handleCancel={closeStrategyGuide}
          title="감소대책 수립 가이드"
          confirmText="확인"
          enableSingleActionButton={true}
          containerSX={{ width: 'auto', height: 'auto' }}
        >
          <Box sx={{ display: 'inline-block'}}>
            {renderGuide()}
          </Box>
        </ModalTemplate>
        {/* 삭제 메시지 */}
        <ConfirmDialog  
          open={deleteModal.isOpen}
          title="정말 삭제하시겠어요?"
          message="삭제하면 복구할 수 없습니다."
          onConfirm={deleteEditData}
          onCancel={closeDeleteModal}
        />
        {/* 데이터 알람 */}
        <SnackbarAlert 
          open={dataAlert.isOpen}
          onClose={closeDataAlert}
          message={dataAlert.message}
          severity={dataAlert.severity} // 'success' | 'error' | 'warning' | 'info'
          //anchorOrigin = {{vertical: "top", horizontal: "center"}}
        />
        {/* 기타 메시지 박스 알람 */}
        <ConfirmDialogSingle  
          open={messageAlarm.isOpen}
          title={messageAlarm.title}
          message={messageAlarm.message}
          onCancel={closeAlarmMessage}
        />
      </TableContainer>
    );
  }
    
  // StrategyRegister 렌더링
  return (
    <Box sx={{ bgcolor: 'var(--mui-palette-background-paper)',height: '100%',display: 'flex',flexDirection: 'column'}}>
      {/* 브레드크럼 + 타이틀 */}
      <BreadcrumbsTable parentTitle={PARENT_TITLE} childTitle={CHILD_TITLE} />
      {/* 테이블 표시 */}
      {renderData()}
    </Box>
  );
}

// 개선전 사진 등록 모달
const RegisterBeforeImage = ({ isOpen, onConfirm, onCancel }) => {
  // 사진 등록 모달 참조
  const imageRef = useRef(null);

  return (
    <ModalTemplate 
      isOpen={isOpen}
      handleConfirm={()=> {
        const image = imageRef.current.getImage();
        onConfirm(image);
      }}
      handleCancel={onCancel}
      confirmText="등록"
      cancelText="취소"
      title="개선전 사진 등록"
      containerSX={{ width: 'auto', height: 'auto' }}
    >
      <ImageUploader ref={imageRef} />
    </ModalTemplate >
  );
};
// 개선전 사진 확대 모달
const ExpandBeforeImage = ({ isOpen, onConfirm, imageURL }) => {
  return(
    <ModalTemplate 
      isOpen={isOpen}
      handleConfirm={onConfirm}
      handleCancel={onConfirm}
      confirmText="확인"
      title="개선전 사진"
      enableSingleActionButton={true}
      contentSX={{
        margin: "12px 0px",
        //width: '25vw',
        //height: '25vh',
        width: 'auto',
        height: 'auto',
        border: `2px dashed ${COLORS.BORDER_DASH}`,
        borderRadius: 2,
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
      }}
    >
      <img 
        src={imageURL || null}
        // src={tempSelectedRowsData.current.improve_before || null}
        alt="Preview"
        style={{
          width: '100%',
          height: '100%',
          //objectFit: 'cover', // 또는 contain
          display: 'block',
          //borderRadius: 'inherit', // Box의 borderRadius 따라감
        }}
      />
    </ModalTemplate >
  );
};

// 담당자 검색 모달
const SearchEmployee = ({ isOpen, onConfirm, onCancel }) =>{
  const { group_code, project_code } = useParams();
  const theme = useTheme();
  ////////////////////////
  // CRUD 요청
  ////////////////////////

  // 직원 조회 요청
  const getEmployeeData = useGetTableData(
    "request",
    "/getStandardEmployee",
    group_code,
    project_code,
  );

  // 메시지 박스의 알람 메시지
  const [messageAlarm, setMessageAlarm] = useState({
      isOpen: false,
      title: '',
      message: '',
  });
  const openAlarmMessage = (title='', message='',) => {
    setMessageAlarm({
      ...messageAlarm,
      isOpen: true,
      title: title,
      message: message,
    });
  };
  const closeAlarmMessage = () => {
    setMessageAlarm({
      ...messageAlarm,
      isOpen: false,
      title: '',
      message: '',
    });
  };

  // 테이블 참조
  const tableRef = useRef();
  const clearSelection = () => { tableRef.current?.clearSelection(); };
  const getSelectedRows = () => { return tableRef.current?.getSelectedRows(); };

  // 테이블의 상단 툴바 커스터마이징 관련 내용
  const [showSearch, setShowSearch] = useState(false); // 검색창 열림 여부
  const [globalFilter, setGlobalFilter] = useState(''); // 검색 텍스트 상태
  const customTableTopToolbar = (table) => (
    <Box width="100%" display="flex" justifyContent="space-between" alignItems="center" p={2}>
      {/* 왼쪽 영역 */}
      <Box display="flex" alignItems="center" gap={2}>
        {/* TODO: */}
      </Box>
      {/* 오른쪽 영역 - 기존 내부 액션도 원하면 여기에 직접 렌더링 */}
      <Box display="flex" alignItems="center" gap={2}>
        {/* 검색창 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => setShowSearch((prev) => !prev)}>
            <SearchIcon />
          </IconButton>
      
          <Collapse in={showSearch} orientation="horizontal">
            <TextField
              size="small"
              placeholder="검색"
              value={globalFilter}
              onChange={(e) => {
                setGlobalFilter(e.target.value);
                table.setGlobalFilter(e.target.value);
              }}
              sx={{ minWidth: 200 }}
              variant="outlined"
              slots={{
                input: OutlinedInput,
              }}
              slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            setGlobalFilter('');
                            table.setGlobalFilter('');
                            setShowSearch(false); // 닫기
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }
              }}
            />
          </Collapse>
        </Box>
      </Box>
    </Box>
  );

  return (
    <ModalTemplate 
      isOpen={isOpen}
      handleConfirm={()=> {
        if(getSelectedRows().length <= 0) {
          openAlarmMessage(
            '안내',
            '담당자를 선택해주세요.'
          );
          return;
        }
        if(getSelectedRows().length > 1) {
          openAlarmMessage(
            '안내',
            '담당자는 한 명만 지정할 수 있습니다.'
          );
          clearSelection();
          return;
        }
        onConfirm(getSelectedRows()[0].original);
        //handleEmployeeSelectedReset();
      }}
      handleCancel={onCancel}
      confirmText="등록"
      cancelText="취소"
      title="담당자 등록"
      titleSX={{ borderBottom: 'none' }}
      //contentSX={{ padding: "0px 0px 0px 0px", }}
      containerSX={{ width: '60%', height: '80%' }}
    >
      <NewTableTemplate 
        ref={tableRef}
        columns={strategyEmpSearchColumns}
        data={getEmployeeData.data || []}
        topToolbarContent={customTableTopToolbar}
        enableCustomGlobalFilter={true}
        enabledSelectionAllRow={false}
        templateSX={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "0.375rem", 
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.05)',
        }}
      />
      {/* 기타 메시지 박스 알람 */}
      <ConfirmDialogSingle  
        open={messageAlarm.isOpen}
        title={messageAlarm.title}
        message={messageAlarm.message}
        onCancel={closeAlarmMessage}
      />
    </ModalTemplate >
  );
};
